import React from "react";
import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import treasurePouch from "../assets/treasure-pouch.png";
import ScreenshotAppChrome from "./ScreenshotAppChrome";

function PlayCaret() {
    return (
        <Box component="svg" viewBox="0 0 14 14" aria-hidden sx={{ width: 14, height: 14, display: "block" }}>
            <path d="M4 2.2L12.2 7 4 11.8V2.2Z" fill="#ffffff" />
        </Box>
    );
}

function HomePlayButton({ label }) {
    return (
        <Box
            sx={{
                alignSelf: "stretch",
                mb: 1.5,
                minHeight: 56,
                borderRadius: 999,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                color: "#fff",
                background: "linear-gradient(135deg, #F2A07A 0%, #D9643A 42%, #C2410C 100%)",
                boxShadow: "0 8px 18px rgba(217, 100, 58, 0.38)",
                border: "1px solid rgba(255,255,255,0.42)",
            }}
        >
            <Typography
                component="span"
                sx={{ color: "#fff", fontSize: 16, fontWeight: 800, letterSpacing: "1px", lineHeight: 1 }}
            >
                {label}
            </Typography>
            <PlayCaret />
        </Box>
    );
}

HomePlayButton.propTypes = {
    label: PropTypes.string.isRequired,
};

function JourneyPreview({ imageUrl, gridSize, startingLayout }) {
    const n = Math.max(2, Number(gridSize) || 4);
    const cellCount = n * n;
    const cells =
        Array.isArray(startingLayout) && startingLayout.length === cellCount
            ? startingLayout
            : Array.from({ length: cellCount }, (_, index) => index);
    const emptySlice = cellCount - 1;

    return (
        <Box
            sx={{
                width: "100%",
                height: "100%",
                position: "relative",
                borderRadius: "12px",
                overflow: "hidden",
                bgcolor: "#334155",
            }}
        >
            {cells.map((slice, visual) => {
                const src = Number(slice);
                const srcRow = Math.floor(src / n);
                const srcCol = src % n;
                const row = Math.floor(visual / n);
                const col = visual % n;
                const empty = src === emptySlice;
                const size = `${100 / n}%`;
                return (
                    <Box
                        key={`journey-cell-${visual}`}
                        sx={{
                            position: "absolute",
                            width: size,
                            height: size,
                            left: `${(col * 100) / n}%`,
                            top: `${(row * 100) / n}%`,
                            p: "1px",
                            boxSizing: "border-box",
                        }}
                    >
                        {empty || !imageUrl ? (
                            <Box sx={{ width: "100%", height: "100%", bgcolor: "#1e293b", borderRadius: "2px" }} />
                        ) : (
                            <Box
                                sx={{
                                    width: "100%",
                                    height: "100%",
                                    overflow: "hidden",
                                    borderRadius: "2px",
                                    position: "relative",
                                }}
                            >
                                <Box
                                    component="img"
                                    src={imageUrl}
                                    alt=""
                                    draggable={false}
                                    sx={{
                                        position: "absolute",
                                        width: `${n * 100}%`,
                                        height: `${n * 100}%`,
                                        maxWidth: "none",
                                        left: `${-srcCol * 100}%`,
                                        top: `${-srcRow * 100}%`,
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}

JourneyPreview.propTypes = {
    imageUrl: PropTypes.string,
    gridSize: PropTypes.number,
    startingLayout: PropTypes.arrayOf(PropTypes.number),
};

/**
 * Android home tab: Journey card, today's free puzzle, and the bottom menu.
 */
export default function ScreenshotHomeScene({ imageUrl, gridSize, startingLayout, title }) {
    const journeyTitle = title && title !== "Sliding puzzle" ? title : "Season Journey";

    return (
        <ScreenshotAppChrome active="home">
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    flexDirection: "column",
                    px: 2,
                    pb: 1.5,
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        pt: "22px",
                        mb: 1,
                        flexShrink: 0,
                    }}
                >
                    <Typography
                        component="h1"
                        sx={{
                            m: 0,
                            fontSize: 22,
                            fontWeight: 800,
                            letterSpacing: "-0.3px",
                            lineHeight: 1.15,
                            color: "#0f1419",
                        }}
                    >
                        Journey
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 72 }}>
                        <Box
                            component="img"
                            src={treasurePouch}
                            alt=""
                            sx={{ width: 44, height: 44, objectFit: "contain", display: "block" }}
                        />
                        <Typography
                            component="span"
                            sx={{ mt: 0.5, fontSize: 12, fontWeight: 800, lineHeight: 1.1, color: "#0f1419" }}
                        >
                            12 coins
                        </Typography>
                    </Box>
                </Box>

                <Box
                    sx={{
                        bgcolor: "#ffffff",
                        borderRadius: "20px",
                        px: 2.25,
                        pt: 2.25,
                        pb: 0.5,
                        mb: 1.25,
                        minHeight: 0,
                        flex: "1 1 auto",
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    <Typography
                        component="p"
                        sx={{
                            m: 0,
                            mb: 1,
                            fontSize: 36,
                            fontWeight: 800,
                            letterSpacing: "-0.8px",
                            lineHeight: "40px",
                            color: "#d9643a",
                            flexShrink: 0,
                        }}
                    >
                        Level 1
                    </Typography>
                    <Typography
                        component="p"
                        sx={{
                            m: 0,
                            mb: 1.5,
                            fontSize: 24,
                            fontWeight: 800,
                            letterSpacing: "-0.3px",
                            lineHeight: 1.15,
                            color: "#0f1419",
                            flexShrink: 0,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                        }}
                    >
                        {journeyTitle}
                    </Typography>
                    <Box sx={{ flex: "1 1 auto", minHeight: 0, mb: 1.5, containerType: "size" }}>
                        <Box sx={{ width: "min(100cqw, 100cqh)", aspectRatio: "1", mx: "auto" }}>
                            <JourneyPreview
                                imageUrl={imageUrl}
                                gridSize={gridSize}
                                startingLayout={startingLayout}
                            />
                        </Box>
                    </Box>
                    <HomePlayButton label="START JOURNEY" />
                </Box>

                <Typography
                    component="h2"
                    sx={{
                        m: 0,
                        mb: 1.25,
                        fontSize: 22,
                        fontWeight: 800,
                        letterSpacing: "-0.3px",
                        lineHeight: 1.15,
                        color: "#0f1419",
                        flexShrink: 0,
                    }}
                >
                    Today&apos;s Free puzzle
                </Typography>
                <Box
                    sx={{
                        bgcolor: "#ffffff",
                        borderRadius: "20px",
                        px: 2.25,
                        pt: 2,
                        pb: 0.5,
                        flexShrink: 0,
                    }}
                >
                    <Box
                        sx={{
                            display: "inline-flex",
                            bgcolor: "rgba(22,163,74,0.9)",
                            px: 1,
                            py: 0.5,
                            borderRadius: "6px",
                            mb: 1.25,
                        }}
                    >
                        <Typography
                            component="span"
                            sx={{ color: "#fff", fontSize: 11, fontWeight: 800, lineHeight: 1.2 }}
                        >
                            FREE
                        </Typography>
                    </Box>
                    <Typography
                        component="p"
                        sx={{
                            m: 0,
                            mb: 1.5,
                            fontSize: 24,
                            fontWeight: 800,
                            letterSpacing: "-0.3px",
                            lineHeight: 1.15,
                            color: "#0f1419",
                        }}
                    >
                        Shark
                    </Typography>
                    <HomePlayButton label="PLAY" />
                </Box>
            </Box>
        </ScreenshotAppChrome>
    );
}

ScreenshotHomeScene.propTypes = {
    imageUrl: PropTypes.string,
    gridSize: PropTypes.number,
    startingLayout: PropTypes.arrayOf(PropTypes.number),
    title: PropTypes.string,
};
